import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltModal } from '@cae/cobalt-react/components/modal';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import * as React from 'react';

import type { InstructorRegulationModalProps } from './InstructorRegulationModal.types';

import styles from './InstructorRegulationModal.module.css';

const InstructorRegulationModal = React.memo(
  ({
    isOpen,
    onClose,
  }: Readonly<InstructorRegulationModalProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);

    return (
      <CobaltModal
        title="Regulations"
        isOpen={isOpen}
        size={isMobileOrTablet ? 'large' : 'medium'}
        onClose={onClose}
        data-testid="regulation-modal"
        actionBar={
          <CobaltActionBar
            variant="floating"
            position="right"
            primaryButton={<CobaltButton onClick={onClose}>Done</CobaltButton>}
          />
        }
      >
        <div className={styles.regulationsContent}>
          <div className={styles.regulationsSection}>
            <h3 className={styles.regulationsSectionTitle}>
              Ground school and simulator
            </h3>
            <p>
              The following CAE Training Center Instructors will have completed
              a CAE Instructor Curriculum designed to meet the following:
            </p>
            <ol type="a">
              <li>
                The training and checking requirements outlined in 14 CFR Parts
                135.338 and 135.340.
              </li>
              <li>
                Within the preceding 24 calendar months, have conducted
                simulator instruction under the observation of an FAA inspector
                or a Check Airman.
              </li>
              <li>
                Differences training between the Certificate Holder's training
                program and the CAE Part 142 core curriculum is accomplished by
                the use of the following process: The application of the
                Operator Specific briefing sheet, the Part 135 Operator Specific
                Read File, the use of Operator Specific Standard Operating
                Procedures Flight Maneuvers and Profiles, Normal, Abnormal and
                Emergency Checklists, and the documentation of the flight
                training record certifying the knowledge of and application of
                these Operator Specific training documents.
              </li>
            </ol>
            <p>
              The following CAE Training Center Instructors are eligible to be
              approved by the certificate holder as contract air transportation
              simulator instructors and ground instructors.
            </p>
            <p className={styles.regulationsSectionWarning}>
              Instructors appearing that fail to maintain currency on the
              assigned aircraft will not be utilized for any training event.
            </p>
          </div>
          <div className={styles.regulationsSection}>
            <h3 className={styles.regulationsSectionTitle}>Check airmen</h3>
            <p>
              The following CAE Training Center Evaluators have completed a CAE
              TCE curriculum designed to meet the following:
            </p>
            <ol type="a">
              <li>
                The training and checking qualifications outlined in 14 CFR
                Parts 135.337 and 135.339.
              </li>
              <li>
                Within the preceding 24 calendar months, have conducted a
                simulator proficiency check under the observation of an FAA
                inspector.
              </li>
              <li>
                Differences training between the Certificate Holder's training
                program and the CAE Part 142 core curriculum is accomplished by
                the use of the following processes: The application of the
                Operator Specific briefing sheet, the Part 135 Operator Specific
                Read File, the use of Operator Specific Standard Operating
                Procedures, Flight Maneuvers and Profiles, Normal, Abnormal and
                Emergency Checklists, and the documentation of the flight
                training record certifying the knowledge of and application of
                these Operator Specific training documents.
              </li>
            </ol>
            <p>
              The following CAE SimuFlite Training Center Evaluators are
              eligible to be nominated by the certificate holder as contract
              simulator check airmen.
            </p>
            <p className={styles.regulationsSectionWarning}>
              Instructors appearing that fail to maintain currency on the
              assigned aircraft will not be utilized for any training event.
            </p>
          </div>
        </div>
      </CobaltModal>
    );
  }
);

InstructorRegulationModal.displayName = 'InstructorRegulationModal';

export { InstructorRegulationModal };
